import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import {
  ApolloClient, ApolloProvider, HttpLink, from,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { InMemoryCache } from '@apollo/client/cache';
import { sha256 } from 'crypto-hash';
import jsCookie from 'js-cookie';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { CachePersistor, SessionStorageWrapper } from 'apollo3-cache-persist';
import App from './App';
import { errorLink } from './tools/links';
import {
  updatePlacementsVar,
  updateXSegVar,
  updateUserTypeVar,
  updateCountryVar,
  updateSignifydIdVar,
  updateApplicationContextVar,
} from './context/ApplicationContext';
import { buildBFFUri } from '../tools/uri';
import { getCacheId, getInMemoryCacheSettings } from '../tools/cache';
import persistenceMapper from '../tools/persist/persistenceMapper';
import persistenceRules from '../tools/persist/rules';
import getFrontendId from '../tools/getFrontendId';
import { persistenceManager } from '../tools/persist/PersistenceManager';

/**
 * @name hydrate
 * @public
 * @async
 * @description Hydrates a React component into the DOM with Apollo Client setup.
 * Configures Apollo Client with proper caching, persistence, and GraphQL endpoints.
 * Sets up application context variables from the server-rendered state.
 *
 * @param {object} options - The configuration options for hydration
 * @param {string} options.frontend - The frontend identifier
 * @param {React.ComponentType} options.component - The React component to hydrate
 * @param {string} options.clientId - Unique client identifier
 * @param {boolean} [options.persist=false] - Whether to persist the Apollo cache in session storage
 * @param {boolean} [options.useGlobalPersistence=true]
 *   Whether to use a global persistence key for the cache
 *
 * @returns {Promise<null|React.ReactElement>}
 *  Resolves to the hydrated React component or null if the DOM element is not found
 */
export default async function hydrate({
  frontend,
  component,
  clientId,
  persist = false,
  useGlobalPersistence = true,
}) {
  const Component = component;
  const cacheId = getCacheId(frontend, clientId);
  const frontendId = getFrontendId(frontend, clientId);
  const el = document.getElementById(frontendId);

  // To support a single file js type delivery, need to handle component endpoints
  // existing in js, but not in dom
  if (!el) return null;

  const config = window[`APOLLO_STATE__${cacheId}`] || {};
  const { query } = config;

  config.cookies = jsCookie.get();

  // Update the applicationContextVar and setup the cache
  updateApplicationContextVar(config);

  // Update the xSegVar
  updateXSegVar(config);

  updateUserTypeVar(config);
  updateCountryVar(config);
  updateSignifydIdVar();

  updatePlacementsVar(config, clientId);

  const inMemoryCacheSettings = getInMemoryCacheSettings();
  const cache = new InMemoryCache(inMemoryCacheSettings).restore(config.CACHE);

  if (persist) {
    try {
      const PERSIST_SUFFIX = 'persist-cache';

      const persistKey = !useGlobalPersistence
        ? `${frontendId}-${PERSIST_SUFFIX}`
        : `${PACKAGE.name}-${PERSIST_SUFFIX}`;

      const persistor = new CachePersistor({
        cache,
        debug: process.env.PERSISTENCE_DEBUG_ENABLED || false,
        key: persistKey,
        persistenceMapper: (data) => persistenceMapper(data, persistenceRules),
        storage: new SessionStorageWrapper(window.sessionStorage),
      });

      const { wasPurged } = await persistenceManager.initialize(persistor, persistKey);

      if (!wasPurged) await persistor.restore();
      // eslint-disable-next-line no-console
    } catch (err) { console.log('[Persist Cache Error]: ', err); }
  }

  // Goal uri example: 'http://localhost:51902?storeId=10051&catalogId=10901&langId=-1&brand=anf&store=a-us',
  const uri = buildBFFUri(query);
  const persistedQueriesLink = createPersistedQueryLink({ sha256, useGETForHashedQueries: true });

  /**
   * @name batchHttpLink
   * @private
   * @description Apollo BatchHttpLink instance for batching multiple operations in a single request
   */
  const batchHttpLink = new BatchHttpLink({
    uri,
    credentials: 'same-origin',
    batchMax: 50,
    batchInterval: 10,
  });

  /**
   * @name httpLink
   * @private
   * @description Apollo HttpLink instance for standard GraphQL requests
   */
  const httpLink = new HttpLink({ uri, credentials: 'same-origin' });

  /**
   * @name splitLink
   * @private
   * @description Function to determine which link to use based on operation type
   * @param {Operation} operation - The Apollo operation
   * @returns {boolean} True for mutations or batched queries, false otherwise
   */
  const splitLink = (operation) => {
    const operationType = operation?.query?.definitions?.[0]?.operation;

    if (operationType === 'mutation') return true;

    const { batch = false } = operation.getContext();
    return batch;
  };

  /*
    Batched queries and mutations are made over POST calls and shouldn't be cached.
    persistExtractionLink should be called after the persistedQueriesLink
  */
  const client = new ApolloClient({
    cache,
    link: from([errorLink])
      .split(
        splitLink,
        batchHttpLink,
        persistedQueriesLink.concat(httpLink),
      ),
  });

  return hydrateRoot(
    el,
    <ApolloProvider client={client}>
      <App
        clientId={clientId}
        frontend={frontend}
        persist={persist}
        useGlobalContextKey={useGlobalPersistence}
      >
        <Component clientId={clientId} />
      </App>
    </ApolloProvider>,
  );
}
